import { useEffect, useMemo, useCallback } from 'react';
import {
  Grid,
  TextField,
  Autocomplete,
  Chip,
  Box,
} from '@mui/material';
import type { AnimalInfo } from '../types';
import { speciesData, commonComplaints, normalizeSpecies } from '../utils/speciesData';

interface AnimalInfoFormProps {
  value: AnimalInfo;
  onChange: (value: AnimalInfo) => void;
}

export default function AnimalInfoForm({ value, onChange }: AnimalInfoFormProps) {
  const speciesOptions = useMemo(() => Object.keys(speciesData), []);

  const breeds = useMemo(() => {
    return value.вид ? speciesData[value.вид]?.breeds || [] : [];
  }, [value.вид]);

  const sexes = useMemo(() => {
    return value.вид ? speciesData[value.вид]?.sex || ['Самец', 'Самка', 'Неизвестно'] : ['Самец', 'Самка', 'Неизвестно'];
  }, [value.вид]);

  // Автозаполнение: когда приходят данные извне, нормализуем вид/пол
  useEffect(() => {
    const normalizedSpecies = normalizeSpecies(value.вид || '');
    if (normalizedSpecies && normalizedSpecies !== value.вид) {
      onChange({ ...value, вид: normalizedSpecies });
    }
  }, []);

  const handleSpeciesChange = useCallback(
    (_: any, newValue: string | null) => {
      const species = newValue || '';
      onChange({
        ...value,
        вид: species,
        порода: '',
        пол: '',
      });
    },
    [value, onChange]
  );

  const handleChange = useCallback(
    (field: keyof AnimalInfo) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...value, [field]: e.target.value });
    },
    [value, onChange]
  );

  const handleSexChange = useCallback(
    (_: any, newValue: string | null) => {
      onChange({ ...value, пол: newValue || '' });
    },
    [value, onChange]
  );

  const handleBreedChange = useCallback(
    (_: any, newValue: string | null) => {
      onChange({ ...value, порода: newValue || '' });
    },
    [value, onChange]
  );

  const complaints = useMemo(() => {
    return (value.жалобы || '').split(',').map((s) => s.trim()).filter(Boolean);
  }, [value.жалобы]);

  const handleComplaintsChange = useCallback(
    (_: any, newValue: string[]) => {
      onChange({ ...value, жалобы: newValue.join(', ') });
    },
    [value, onChange]
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={4}>
        <TextField
          fullWidth
          label="Кличка"
          value={value.кличка || ''}
          onChange={handleChange('кличка')}
          placeholder="Например, Бобик"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Autocomplete
          freeSolo
          options={speciesOptions}
          value={value.вид || ''}
          onChange={handleSpeciesChange}
          renderInput={(params) => <TextField {...params} label="Вид" placeholder="Например, Собака" />}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Autocomplete
          freeSolo
          options={breeds}
          value={value.порода || ''}
          onChange={handleBreedChange}
          disabled={!value.вид}
          renderInput={(params) => <TextField {...params} label="Порода" placeholder="Например, Лабрадор" />}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <TextField
          fullWidth
          label="Возраст"
          value={value.возраст || ''}
          onChange={handleChange('возраст')}
          placeholder="например, 3 года"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Autocomplete
          freeSolo
          options={sexes}
          value={value.пол || ''}
          onChange={handleSexChange}
          disabled={!value.вид}
          renderInput={(params) => <TextField {...params} label="Пол" placeholder="Например, Кобель" />}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <TextField
          fullWidth
          label="Вес"
          value={value.вес || ''}
          onChange={handleChange('вес')}
          placeholder="например, 25 кг"
        />
      </Grid>

      <Grid item xs={12}>
        <Box>
          <Autocomplete
            multiple
            freeSolo
            options={commonComplaints}
            value={complaints}
            onChange={handleComplaintsChange}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Жалобы"
                placeholder="Выберите или введите жалобу"
              />
            )}
          />
        </Box>
      </Grid>
    </Grid>
  );
}
